import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
const privacyContent = {
  title: 'Política de Privacidad',
  body: `Casa Calma Candle Co. respeta tu privacidad. Esta política describe cómo recopilamos, usamos y protegemos tu información personal.

**Información que recopilamos:**
Recopilamos únicamente la información necesaria para procesar tu compra: nombre, dirección de correo electrónico y datos de pago. No almacenamos datos de tarjetas de crédito directamente; estos son procesados de forma segura por nuestro proveedor de pagos.

**Uso de la información:**
Tu información se utiliza exclusivamente para:
• Procesar y entregar tu compra digital (PDF).
• Enviarte el enlace de descarga por correo electrónico.
• Comunicarnos contigo en caso de problemas con tu pedido.

**Compartir información:**
No vendemos, alquilamos ni compartimos tu información personal con terceros, excepto cuando sea necesario para procesar tu pago.

**Cookies:**
Nuestro sitio puede utilizar cookies básicas para mejorar tu experiencia de navegación y analizar el tráfico del sitio.

**Tus derechos:**
Puedes solicitar la eliminación de tus datos personales en cualquier momento enviándonos un correo electrónico.

**Cambios a esta política:**
Nos reservamos el derecho de actualizar esta política en cualquier momento. Los cambios serán publicados en esta página.

Última actualización: Marzo 2026.`
};
const termsContent = {
  title: 'Términos y Condiciones',
  body: `Al comprar y descargar productos de Casa Calma Candle Co., aceptas los siguientes términos y condiciones.

**Producto digital:**
Todos los productos ofrecidos son archivos digitales en formato PDF. No se incluyen materiales físicos de ningún tipo. La entrega es inmediata por correo electrónico tras confirmar el pago.

**Uso permitido:**
El contenido es para uso personal únicamente. Queda prohibida la reproducción, distribución, reventa o compartición del material con terceros sin autorización expresa.

**Política de reembolsos:**
Debido a la naturaleza digital del producto, no se aceptan devoluciones ni reembolsos una vez completada la descarga. Si experimentas problemas técnicos con la descarga, contáctanos y lo resolveremos en un plazo de 24 horas.

**Propiedad intelectual:**
Todo el contenido, incluyendo textos, diseños, fórmulas, plantillas y materiales gráficos, es propiedad de Casa Calma Candle Co. y está protegido por las leyes de derechos de autor.

**Resultados:**
Los resultados pueden variar según la dedicación, materiales utilizados y condiciones individuales. No garantizamos ingresos específicos ni resultados de ventas.

**Limitación de responsabilidad:**
Casa Calma Candle Co. no se hace responsable de daños directos o indirectos derivados del uso de los materiales proporcionados. La elaboración de velas implica el uso de calor y materiales que requieren precaución.

**Jurisdicción:**
Estos términos se rigen por las leyes de los Estados Unidos de América.

Última actualización: Marzo 2026.`
};
function LegalModal({
  isOpen,
  onClose,
  title,
  body
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  body: string;
}) {
  return <AnimatePresence data-id="element-35">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-36">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" data-id="element-37" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        duration: 0.2
      }} onClick={e => e.stopPropagation()} className="relative bg-brand-bg rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden border border-brand-line/30" data-id="element-38">
            <div className="flex items-center justify-between p-6 border-b border-brand-line/20 bg-brand-alt/50" data-id="element-39">
              <h3 className="text-xl font-bold text-brand-text font-heading" data-id="element-40">
                {title}
              </h3>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-brand-accent/30 transition-colors" aria-label="Cerrar" data-id="element-41">
                <X className="w-5 h-5 text-brand-subtext" data-id="element-42" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-43">
              <div className="text-brand-subtext leading-relaxed whitespace-pre-line text-sm" data-id="element-44">
                {body.split('**').map((segment, i) => i % 2 === 1 ? <strong key={i} className="text-brand-text font-semibold" data-id="element-45">
                      {segment}
                    </strong> : <span key={i} data-id="element-46">{segment}</span>)}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  return <>
      <footer className="bg-brand-text text-brand-bg py-12" data-id="element-47">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-48">
          <p className="text-sm tracking-[0.2em] font-semibold uppercase mb-4 text-brand-accent" data-id="element-49">
            Casa Calma Candle
          </p>
          <p className="text-brand-alt/70 font-heading italic text-lg mb-6" data-id="element-50">
            Aromas que abrazan tu hogar. 🕯️
          </p>
          <div className="flex items-center justify-center gap-4 text-sm text-brand-alt/50" data-id="element-51">
            <button onClick={() => setActiveModal('privacy')} className="hover:text-brand-accent transition-colors underline underline-offset-2" data-id="element-52">
              Política de Privacidad
            </button>
            <span data-id="element-53">|</span>
            <button onClick={() => setActiveModal('terms')} className="hover:text-brand-accent transition-colors underline underline-offset-2" data-id="element-54">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title={privacyContent.title} body={privacyContent.body} data-id="element-55" />
      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title={termsContent.title} body={termsContent.body} data-id="element-56" />
    </>;
}